import { cn } from "../../../utils/cn.js";
import { ScrollArea, ScrollAreaContent, ScrollAreaViewport } from "../ScrollArea/ScrollArea.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import { AvatarFallback, AvatarImage, AvatarLoading, AvatarRoot } from "../Avatar/Avatar.js";
import { Button } from "../Button/Button.js";
import { Tooltip, TooltipContent, TooltipLabel, TooltipProvider, TooltipTrigger } from "../Tooltip/Tooltip.js";
import { cva } from "class-variance-authority";
import { CaretDownIcon, CaretLeftIcon, SidebarSimpleIcon } from "@phosphor-icons/react";
import * as React from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useRender } from "@base-ui/react/use-render";
import { Collapsible } from "@base-ui/react/collapsible";
//#region src/components/Sidebar/Sidebar.tsx
/**
* Icon-rail width when the sidebar is collapsed.
*
* Chosen so item icons are already centered where they sit when expanded:
* nav `p-md` (16) + item `p-xs` (8) puts a 16px icon's center at x=32 — the
* exact center of a 64px rail. Collapse therefore only animates the width;
* item icons never move. The 40px logo overhangs the 32px column
* symmetrically via its own margin slide.
*/
const SIDEBAR_COLLAPSED_WIDTH = "64px";
/** Hover delay before rail tooltips open (shared via the root provider). */
const SIDEBAR_TOOLTIP_DELAY_MS = 200;
/**
* Width transition for rail collapse/expand. Also the exit window for labels
* and sections that fade out under the shrinking rail, and the settle timer
* that gates rail hover affordances.
*/
const SIDEBAR_COLLAPSE_DURATION_MS = 350;
/**
* Sidebar motion tokens, exposed as CSS variables on the root `<nav>` so every
* subcomponent can reference the same values through Tailwind's
* parenthesised custom-property utilities instead of repeating literals.
*/
const SIDEBAR_MOTION_STYLE = {
	"--sidebar-collapsed-width": SIDEBAR_COLLAPSED_WIDTH,
	"--sidebar-duration": `350ms`,
	"--sidebar-ease": "cubic-bezier(0.22, 1, 0.36, 1)",
	"--sidebar-caret-ease": "cubic-bezier(0.34, 1.3, 0.64, 1)",
	"--sidebar-label-delay": `100ms`
};
/**
* Row typography shared by items, section titles, the header title and the
* back control: `body-medium`, which the token export defines as 14px on a
* 21px line, tracked at -0.4px (`tracking-base`).
*
* Note the scale: `body-medium` (14px) is the larger size and `body-base`
* (12px) the smaller, the reverse of what the names suggest. The nav rows
* are the 14px step.
*
* `tracking-base` specifically, not an arbitrary value: product typography
* forces `tracking-sm` (-0.2px) onto `text-body-*` unless the element carries
* one of the named tracking classes, and that rule outranks an arbitrary
* `tracking-[…]`.
*/
const SIDEBAR_ROW_TEXT = "text-body-medium leading-body-medium font-heading-medium tracking-base";
const SidebarCollapseContext = React.createContext(null);
/**
* Reads sidebar collapse state from the nearest `SidebarProvider`.
*
* @returns Collapse state helpers, or expanded defaults when no provider is present
*/
function useSidebarCollapse() {
	const context = React.useContext(SidebarCollapseContext);
	if (!context) return {
		collapsed: false,
		settledCollapsed: false,
		expanding: false,
		railHoverArmed: false,
		armRailHover: () => void 0,
		setCollapsed: () => void 0,
		toggleCollapsed: () => void 0
	};
	return context;
}
/**
* Keeps content mounted through its exit fade: returns true while visible and
* for `durationMs` after visibility drops, so labels fade out in place and
* only then leave the layout (no mid-fade squeeze, no leftover flex space).
*
* @param visible - Whether the content should currently be shown
* @param durationMs - Exit transition duration to wait before unmounting
* @returns Whether the content should stay mounted
*/
function useDelayedPresence(visible, durationMs = 350) {
	const [mounted, setMounted] = React.useState(visible);
	React.useEffect(() => {
		if (visible) {
			setMounted(true);
			return;
		}
		const timer = window.setTimeout(() => setMounted(false), durationMs);
		return () => window.clearTimeout(timer);
	}, [visible, durationMs]);
	return mounted;
}
/**
* True only after the collapse width animation has finished. Clears immediately
* on expand.
*
* @param collapsed - Current collapsed state from the provider
* @param durationMs - Matches the sidebar width transition duration
* @returns Whether collapse has settled
*/
function useSettledCollapsed(collapsed, durationMs = 350) {
	const [settledCollapsed, setSettledCollapsed] = React.useState(collapsed);
	React.useEffect(() => {
		if (!collapsed) {
			setSettledCollapsed(false);
			return;
		}
		const timer = window.setTimeout(() => setSettledCollapsed(true), durationMs);
		return () => window.clearTimeout(timer);
	}, [collapsed, durationMs]);
	return settledCollapsed;
}
/**
* True from the moment the rail starts expanding until its width transition
* and the label lead have run.
*
* Derived during render (React's "adjust state on prop change" pattern) so
* the labels that remount on the same commit already see `true`.
*/
function useRailExpanding(collapsed, durationMs = 450) {
	const [state, setState] = React.useState({
		collapsed,
		expanding: false
	});
	if (state.collapsed !== collapsed) setState({
		collapsed,
		expanding: !collapsed
	});
	React.useEffect(() => {
		if (!state.expanding) return;
		const timer = window.setTimeout(() => setState((current) => ({
			...current,
			expanding: false
		})), durationMs);
		return () => window.clearTimeout(timer);
	}, [state.expanding, durationMs]);
	return state.collapsed === collapsed ? state.expanding : !collapsed;
}
/**
* Fade treatment for labels that disappear into the icon rail: fades in place
* (the shrinking rail clips the text — no width squeeze). Pair with
* `useDelayedPresence` so the element unmounts only after the exit fade
* completes.
*
* The fade back in is scoped to `entering` — the rail-expand window. Then it
* starts transparent and waits `--sidebar-label-delay`, so text lands once
* the rail has room for it. Outside that window a label is simply there:
* text in a pane that is sliding in moves with the pane rather than resetting
* to transparent and catching up a beat later.
*/
const sidebarCollapseFadeVariants = cva("transition-opacity duration-(--sidebar-duration) ease-(--sidebar-ease)", {
	compoundVariants: [{
		className: "starting:opacity-0 delay-(--sidebar-label-delay)",
		collapsed: false,
		entering: true
	}],
	defaultVariants: { entering: false },
	variants: {
		collapsed: {
			false: "opacity-100",
			true: "pointer-events-none opacity-0 delay-0"
		},
		entering: {
			false: "",
			true: ""
		}
	}
});
/**
* Provides collapse state for a Sidebar tree.
*
* Prefer this over threading `collapsed` through every subcomponent. The root
* `Sidebar` also accepts a `collapsed` prop for one-off controlled usage.
*
* @param props.collapsed - Controlled collapsed state
* @param props.defaultCollapsed - Uncontrolled initial state
* @param props.onCollapsedChange - Change callback for both modes
* @returns Context provider wrapping children
*/
function SidebarProvider({ collapsed: collapsedProp, defaultCollapsed = false, onCollapsedChange, children }) {
	const [uncontrolledCollapsed, setUncontrolledCollapsed] = React.useState(defaultCollapsed);
	const isControlled = collapsedProp !== void 0;
	const collapsed = isControlled ? collapsedProp : uncontrolledCollapsed;
	const settledCollapsed = useSettledCollapsed(collapsed);
	const expanding = useRailExpanding(collapsed);
	const [railHoverArmed, setRailHoverArmed] = React.useState(false);
	const setCollapsed = React.useCallback((next) => {
		if (!isControlled) setUncontrolledCollapsed(next);
		onCollapsedChange?.(next);
	}, [isControlled, onCollapsedChange]);
	const toggleCollapsed = React.useCallback(() => {
		setCollapsed(!collapsed);
	}, [collapsed, setCollapsed]);
	const armRailHover = React.useCallback(() => {
		setRailHoverArmed(true);
	}, []);
	React.useEffect(() => {
		if (!collapsed) setRailHoverArmed(false);
	}, [collapsed]);
	const value = React.useMemo(() => ({
		collapsed,
		settledCollapsed,
		expanding,
		railHoverArmed,
		armRailHover,
		setCollapsed,
		toggleCollapsed
	}), [
		collapsed,
		settledCollapsed,
		expanding,
		railHoverArmed,
		armRailHover,
		setCollapsed,
		toggleCollapsed
	]);
	return /* @__PURE__ */ jsx(SidebarCollapseContext.Provider, {
		value,
		children
	});
}
/**
* Root container for the sidebar. Renders a full-height `<nav>` with the
* base surface background and the standard side-nav width token.
*
* When collapsed (via prop or `SidebarProvider`), width animates to the icon
* rail. Labels fade out without shrinking so icons do not shift.
*
* Also owns the rail-hover arming lifecycle: after collapse settles, residual
* pointer hover must not flash the expand affordance — hover is armed only
* once the pointer leaves the nav (or settle finishes with the pointer
* already outside).
*
* @param props - Standard nav element props plus optional collapsed override
* @returns The rendered sidebar root
*/
function Sidebar({ className, collapsed: collapsedProp, onPointerLeave, ...props }) {
	const { collapsed: collapsedFromContext, settledCollapsed, armRailHover } = useSidebarCollapse();
	const collapsed = collapsedProp ?? collapsedFromContext;
	const navRef = React.useRef(null);
	React.useEffect(() => {
		if (!settledCollapsed) return;
		if (navRef.current && !navRef.current.matches(":hover")) armRailHover();
	}, [settledCollapsed, armRailHover]);
	return /* @__PURE__ */ jsx(TooltipProvider, {
		delay: SIDEBAR_TOOLTIP_DELAY_MS,
		children: /* @__PURE__ */ jsx("nav", {
			ref: navRef,
			className: cn("group/sidebar flex h-full flex-col gap-lg overflow-hidden bg-elevation-surface-base p-md font-body", "transition-[width] duration-(--sidebar-duration) ease-(--sidebar-ease)", "[--color-elevation-surface-base-hover:var(--color-neutral-300)]", "[--color-elevation-surface-base-pressed:var(--color-neutral-400)]", "dark:[--color-elevation-surface-base-hover:var(--color-darkneutral-100)]", "dark:[--color-elevation-surface-base-pressed:var(--color-darkneutral-200)]", collapsed ? "w-(--sidebar-collapsed-width)" : "w-(--layout-grid-side-nav-width)", className),
			"data-collapsed": collapsed ? "true" : void 0,
			style: SIDEBAR_MOTION_STYLE,
			onPointerLeave: (event) => {
				if (settledCollapsed) armRailHover();
				onPointerLeave?.(event);
			},
			...props
		})
	});
}
/**
* Quick crossfade. The leaving pane fades out over 100ms with a 4px slide.
* The arriving pane starts 40ms in, so the two overlap briefly, and fades in
* over 200ms from 8px. The leaving pane unmounts when its fade ends.
*/
const SIDEBAR_PANE_EXIT_MS = 100;
/** Exit plus enter. They overlap; the swap is done when the enter finishes. */
const SIDEBAR_PANE_DURATION_MS = 240;
const SidebarPanesContext = React.createContext(null);
/**
* Shows one `SidebarPane` at a time and animates the handoff between them.
*
* This is how a nested navigation level (the settings pane, "Back to app")
* lives inside the same `Sidebar`: each level is a pane holding its own
* header, body and footer; the root keeps its width, surface and collapse
* state while the contents turn like a page. Direction is derived from the
* panes' `depth` props, so going deeper slides in from the right and coming
* back slides in from the left.
*
* Panes stay mounted through their exit transition and unmount afterwards, so
* only the visible pane is in the tab order once the swap settles.
*
* @param props.value - Identifier of the pane to show
* @returns The pane stack
*
* @example
* ```tsx
* <Sidebar>
*   <SidebarPanes value={inSettings ? 'settings' : 'app'}>
*     <SidebarPane value="app" depth={0}>…</SidebarPane>
*     <SidebarPane value="settings" depth={1}>…</SidebarPane>
*   </SidebarPanes>
* </Sidebar>
* ```
*/
function SidebarPanes({ className, value, ...props }) {
	const depths = React.useRef(/* @__PURE__ */ new Map());
	const [travel, setTravel] = React.useState({
		value,
		direction: "forward",
		swapped: false
	});
	const registerDepth = React.useCallback((pane, depth) => {
		depths.current.set(pane, depth);
	}, []);
	if (travel.value !== value) {
		const from = depths.current.get(travel.value) ?? 0;
		setTravel({
			value,
			direction: (depths.current.get(value) ?? 0) < from ? "back" : "forward",
			swapped: true
		});
	}
	const direction = travel.value === value ? travel.direction : "forward";
	const swapped = travel.swapped || travel.value !== value;
	const context = React.useMemo(() => ({
		value,
		direction,
		swapped,
		registerDepth
	}), [
		value,
		direction,
		swapped,
		registerDepth
	]);
	return /* @__PURE__ */ jsx(SidebarPanesContext.Provider, {
		value: context,
		children: /* @__PURE__ */ jsx("div", {
			className: cn("relative flex min-h-0 flex-1 flex-col", className),
			"data-direction": direction,
			...props
		})
	});
}
/**
* One level of sidebar navigation inside `SidebarPanes`. Lays out its
* children like the `Sidebar` root does (column, `gap-lg`) so a pane can hold
* a `SidebarHeader`, `SidebarBody` and `SidebarFooter` of its own.
*
* Quick crossfade. The leaving pane fades out over 100ms with a 4px slide
* and is inert (`aria-hidden`, no pointer events) until it unmounts. The
* arriving pane fades in over 200ms from 8px, starting 40ms after the leave
* begins, on the sidebar curve. The pane present on first paint renders
* without any of this.
*
* @param props.value - Pane identifier
* @param props.depth - Nesting level for slide direction
* @returns The pane, or null once it has finished leaving
*/
function SidebarPane({ className, value, depth = 0, ...props }) {
	const context = React.useContext(SidebarPanesContext);
	if (!context) throw new Error("SidebarPane must be rendered inside SidebarPanes");
	const { value: activeValue, direction, swapped, registerDepth } = context;
	const active = activeValue === value;
	const mounted = useDelayedPresence(active, SIDEBAR_PANE_EXIT_MS);
	registerDepth(value, depth);
	if (!mounted) return null;
	return /* @__PURE__ */ jsx("div", {
		"aria-hidden": active ? void 0 : true,
		className: cn("absolute inset-0 flex flex-col gap-lg transition-[opacity,translate]", active ? cn("translate-x-0 opacity-100", swapped && cn("duration-[200ms] delay-[40ms] ease-(--sidebar-ease) starting:opacity-0", direction === "forward" ? "starting:translate-x-xs" : "starting:-translate-x-xs")) : cn("pointer-events-none opacity-0 duration-[100ms] ease-out", direction === "forward" ? "-translate-x-xxs" : "translate-x-xxs"), className),
		"data-pane": value,
		"data-state": active ? "active" : "leaving",
		...props
	});
}
/**
* Header control for a nested pane: a small caret and the parent level's
* name in secondary text. Takes the place of the logo row in that pane's
* `SidebarHeader`.
*
* Full width, with the same padding and type as a `SidebarItem`, so the caret
* lines up with the icons below it and the hover wash covers the whole row.
*
* Supports `render` for router links the same way `SidebarItem` does.
*
* @param props.children - Label naming where "back" leads
* @returns The rendered back control
*/
function SidebarBackButton(props) {
	const { children, className, render, ref, ...rest } = props;
	return useRender({
		defaultTagName: "button",
		props: {
			children: /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(CaretLeftIcon, { className: "size-[14px] shrink-0 text-icon-secondary" }), /* @__PURE__ */ jsx("span", {
				className: "truncate",
				children
			})] }),
			className: cn("flex w-full min-w-0 cursor-pointer items-center gap-xs rounded-sm border-0 bg-transparent px-xs py-[7px] text-left no-underline hover:no-underline", "text-text-secondary", SIDEBAR_ROW_TEXT, "transition-colors duration-150 ease-(--sidebar-ease) hover:bg-elevation-surface-base-hover active:bg-elevation-surface-base-pressed", "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-text-selected", className),
			...render ? {} : { type: "button" },
			...rest
		},
		ref,
		render
	});
}
/**
* Header area containing the team logo, name, and optional subtitle.
* Renders a horizontal flex row with center-aligned children.
*
* Stays a single row when collapsed: the logo doubles as the collapse toggle
* (`SidebarRailToggle`) and the text block collapses to zero width beside it,
* so the same markup serves both the expanded header and the icon rail.
*
* @param props - Standard div props
* @returns The rendered header container
*/
function SidebarHeader({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("flex min-h-[40px] shrink-0 items-center gap-xs", className),
		...props
	});
}
const SIDEBAR_LOGO_FALLBACK_COLOR_CLASSES = [
	"bg-icon-brand text-text-primary-inverse",
	"bg-icon-information text-text-primary-inverse",
	"bg-icon-success text-text-primary-inverse",
	"bg-icon-warning text-text-primary",
	"bg-icon-danger text-text-primary-inverse",
	"bg-icon-accent-brickwork text-text-primary-inverse",
	"bg-icon-accent-cement text-text-primary",
	"bg-icon-accent-craneberry text-text-primary-inverse",
	"bg-icon-accent-hivis text-text-primary"
];
/**
* Generates initials from a team/account name for sidebar logo fallbacks.
*
* Uses the first character from the first and last words. Single-word names
* fall back to the first two alphanumeric characters.
*
* @param name - Team or account name to derive initials from
* @returns One or two uppercase initials, or an empty string when the name is blank
*/
function getSidebarLogoInitials(name) {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (!parts.length) return "";
	if (parts.length === 1) return Array.from(parts[0].replace(/[^\p{L}\p{N}]/gu, "")).slice(0, 2).join("").toUpperCase();
	return `${parts[0][0] ?? ""}${parts.at(-1)?.[0] ?? ""}`.toUpperCase();
}
/**
* Maps a name to a deterministic fallback token class pair.
*
* @param seed - Stable seed used as the hash input
* @returns Background and foreground token classes for the initials tile
*/
function getSidebarLogoFallbackColorClasses(seed) {
	let hash = 0;
	for (const character of seed) hash = hash * 31 + character.charCodeAt(0) >>> 0;
	return SIDEBAR_LOGO_FALLBACK_COLOR_CLASSES[hash % SIDEBAR_LOGO_FALLBACK_COLOR_CLASSES.length];
}
function SidebarLogoInitials({ name, seed, className }) {
	const normalizedName = name?.trim() ?? "";
	const normalizedSeed = seed?.trim() || normalizedName;
	const fallbackInitials = normalizedName ? getSidebarLogoInitials(normalizedName) : void 0;
	const fallbackColorClasses = fallbackInitials ? getSidebarLogoFallbackColorClasses(normalizedSeed) : void 0;
	if (!fallbackInitials) return null;
	return /* @__PURE__ */ jsx("span", {
		className: cn("flex size-full items-center justify-center rounded-[inherit] text-body-small leading-body-small font-heading-semi-bold uppercase", fallbackColorClasses, className),
		children: fallbackInitials
	});
}
/**
* 40x40 clip surface for sidebar logo content.
*
* Owns `overflow-hidden rounded-xs`; nested image layers should use
* `overflow-visible rounded-[inherit]` so corners are not clipped twice.
*/
function SidebarLogo({ children, className, name, seed, ...props }) {
	const normalizedName = name?.trim() ?? "";
	const normalizedSeed = seed?.trim() || normalizedName;
	const hasChildren = React.Children.count(children) > 0;
	const fallbackInitials = !hasChildren && normalizedName ? getSidebarLogoInitials(normalizedName) : void 0;
	return /* @__PURE__ */ jsx("div", {
		className: cn("relative flex size-[40px] shrink-0 items-center justify-center overflow-hidden rounded-xs border-(length:--border-width-default) border-solid border-border-primary bg-elevation-surface-raised", fallbackInitials && "border-transparent bg-transparent", className),
		...props,
		children: hasChildren ? children : fallbackInitials ? /* @__PURE__ */ jsx(SidebarLogoInitials, {
			name: normalizedName,
			seed: normalizedSeed
		}) : null
	});
}
/**
* Remote logo for `SidebarLogo` with skeleton loading and initials fallback.
*
* The tile stays `neutral-0` in dark mode. The image is contained inside
* a 2px inset so a wide lockup (Sherwin-Williams) stays fully visible.
*
* @param props.name - Team or account name for initials on load failure
* @param props.seed - Optional stable seed for fallback color hashing
* @returns The rendered logo tile
*/
function SidebarLogoImage({ name, seed, alt = "", style, ...imageProps }) {
	return /* @__PURE__ */ jsx(SidebarLogo, {
		className: "bg-neutral-0 has-[[data-slot=avatar-loading]]:bg-neutral-0",
		children: /* @__PURE__ */ jsxs(AvatarRoot, {
			className: "flex size-full items-center justify-center overflow-visible rounded-[inherit] bg-transparent p-[2px]",
			children: [
				/* @__PURE__ */ jsx(AvatarImage, {
					alt,
					className: "object-contain",
					style: {
						height: "auto",
						maxHeight: "100%",
						maxWidth: "100%",
						objectFit: "contain",
						width: "auto",
						...style
					},
					...imageProps
				}),
				/* @__PURE__ */ jsx(AvatarLoading, {
					className: "bg-neutral-100",
					children: /* @__PURE__ */ jsx(Skeleton, {
						className: "size-full bg-neutral-100",
						radius: "none"
					})
				}),
				/* @__PURE__ */ jsx(AvatarFallback, { children: /* @__PURE__ */ jsx(SidebarLogoInitials, {
					name,
					seed
				}) })
			]
		})
	});
}
/**
* Vertical stack for the team name and subtitle text beside the logo.
*
* Fades out when the sidebar collapses without changing layout width, so the
* logo does not shift as labels disappear under the clipping rail.
*
* @param props - Standard div props; expects SidebarHeaderTitle and SidebarHeaderSubtitle as children
* @returns The rendered info container
*/
function SidebarHeaderInfo({ className, ...props }) {
	const { collapsed, expanding } = useSidebarCollapse();
	if (!useDelayedPresence(!collapsed)) return null;
	return /* @__PURE__ */ jsx("div", {
		className: cn("flex min-w-0 flex-1 flex-col justify-center", sidebarCollapseFadeVariants({
			collapsed,
			entering: expanding
		}), className),
		...props
	});
}
/**
* Icon button that toggles sidebar collapsed state.
*
* Place to the right of the account header when expanded. Uses
* `SidebarProvider` (or a controlled `Sidebar` + external state) for the
* toggle target.
*
* Hover/press use the same elevation surface tokens as `SidebarItem`. Tertiary
* button hover (`background-neutral-secondary-hover`) is an alpha wash of the
* same neutral as `elevation-surface-base`, so it is effectively invisible on
* the sidebar in light mode.
*
* @param props.expandLabel - aria-label used while collapsed
* @param props.collapseLabel - aria-label used while expanded
* @returns The rendered collapse/expand control
*/
function SidebarCollapseTrigger({ className, expandLabel = "Expand sidebar", collapseLabel = "Collapse sidebar", onClick, ...props }) {
	const { collapsed, toggleCollapsed } = useSidebarCollapse();
	const label = collapsed ? expandLabel : collapseLabel;
	return /* @__PURE__ */ jsxs(Tooltip, { children: [/* @__PURE__ */ jsx(TooltipTrigger, { render: /* @__PURE__ */ jsx(Button, {
		"aria-expanded": !collapsed,
		"aria-label": label,
		className: cn("shrink-0 [&_svg]:size-md", "hover:bg-elevation-surface-base-hover active:bg-elevation-surface-base-pressed", className),
		shape: "icon",
		size: "base",
		type: "button",
		variant: "tertiary",
		onClick: (event) => {
			toggleCollapsed();
			onClick?.(event);
		},
		...props,
		children: /* @__PURE__ */ jsx(SidebarSimpleIcon, {
			className: cn("transition-transform duration-(--sidebar-duration) ease-(--sidebar-ease)", collapsed && "scale-x-[-1]"),
			weight: collapsed ? "fill" : "regular"
		})
	}) }), /* @__PURE__ */ jsx(TooltipContent, {
		side: collapsed ? "right" : "bottom",
		align: "center",
		sideOffset: collapsed ? 12 : 8,
		children: /* @__PURE__ */ jsx(TooltipLabel, { children: label })
	})] });
}
/**
* Active expand-affordance selectors for the collapsed rail logo.
*
* Popup-open / focus-visible do not require hover-arming (menus portal outside
* the nav; keyboard focus is intentional). Hover classes are applied separately
* only once `railHoverArmed` is true — see SidebarRailToggle.
*/
const sidebarRailAffordancePopupOrFocusActive = "group-has-[[data-popup-open]]/sidebar:opacity-0 group-focus-visible/rail-toggle:opacity-0";
const sidebarRailAffordancePopupOrFocusVisible = "group-has-[[data-popup-open]]/sidebar:opacity-100 group-focus-visible/rail-toggle:opacity-100";
const sidebarRailAffordanceHoverActive = "group-hover/sidebar:opacity-0";
const sidebarRailAffordanceHoverVisible = "group-hover/sidebar:opacity-100";
/**
* The brand/logo tile as the rail's expand control. While the sidebar is
* expanded it renders as a plain, inert logo tile; once collapsed it becomes
* the toggle — hovering anywhere on the collapsed sidebar (or focusing the
* control) crossfades the logo into the sidebar icon via opacity only, and
* clicking expands via the nearest `SidebarProvider`. Open rail menus (Base UI
* `data-popup-open` on their triggers) keep the same affordance while the
* pointer is in the portaled popup. Direct hover/press on the control itself
* also applies the same surface shading as other rail items; the tooltip
* remains button-only.
*
* Hover affordances wait until collapse has settled *and* the pointer has
* left the sidebar once. A settle timer alone is not enough: clicking
* collapse leaves residual `:hover` on the nav, so enabling `group-hover`
* the moment the width transition ends would still flash the expand icon.
*
* On collapse the tile glides onto the rail's icon column: with no header
* horizontal padding, a 4px (`xxs`) pull-back centers the 40px tile on the
* icon axis (nav `p-md` + item `p-xs` → center x=32). It glides back on expand.
*
* @param props.children - Brand tile rendered at rest
* @param props.expandLabel - aria-label while collapsed
* @returns The rendered rail toggle button
*/
function SidebarRailToggle({ children, className, expandLabel = "Expand sidebar", onClick, ...props }) {
	const { collapsed, settledCollapsed, railHoverArmed, toggleCollapsed } = useSidebarCollapse();
	const hoverAffordanceEnabled = settledCollapsed && railHoverArmed;
	const button = /* @__PURE__ */ jsxs("button", {
		"aria-hidden": collapsed ? void 0 : true,
		"aria-label": collapsed ? expandLabel : void 0,
		className: cn("group/rail-toggle relative flex size-[40px] shrink-0 items-center justify-center rounded-xs border-0 bg-transparent p-0", "transition-[margin] duration-(--sidebar-duration) ease-(--sidebar-ease) group-data-[collapsed=true]/sidebar:-ml-xxs", collapsed ? "cursor-pointer" : "cursor-default", hoverAffordanceEnabled && "hover:bg-elevation-surface-base-hover active:bg-elevation-surface-base-pressed", settledCollapsed && "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-selected", className),
		tabIndex: collapsed ? void 0 : -1,
		type: "button",
		onClick: (event) => {
			if (!collapsed) return;
			toggleCollapsed();
			onClick?.(event);
		},
		...props,
		children: [/* @__PURE__ */ jsx("span", {
			className: cn("absolute inset-0 flex items-center justify-center transition-opacity duration-[280ms] ease-(--sidebar-ease)", settledCollapsed && sidebarRailAffordancePopupOrFocusActive, hoverAffordanceEnabled && sidebarRailAffordanceHoverActive),
			children
		}), /* @__PURE__ */ jsx("span", {
			className: cn("absolute inset-0 flex items-center justify-center rounded-xs opacity-0 transition-opacity duration-[280ms] ease-(--sidebar-ease)", settledCollapsed && sidebarRailAffordancePopupOrFocusVisible, hoverAffordanceEnabled && sidebarRailAffordanceHoverVisible),
			children: /* @__PURE__ */ jsx(SidebarSimpleIcon, { className: "size-md text-icon-primary" })
		})]
	});
	return /* @__PURE__ */ jsxs(Tooltip, {
		disabled: !settledCollapsed,
		children: [/* @__PURE__ */ jsx(TooltipTrigger, { render: button }), /* @__PURE__ */ jsx(TooltipContent, {
			side: "right",
			align: "center",
			sideOffset: 12,
			children: /* @__PURE__ */ jsx(TooltipLabel, { children: expandLabel })
		})]
	});
}
/**
* Team/company name rendered in body/medium typography.
*
* @param props - Standard span props
* @returns The rendered title text
*/
function SidebarHeaderTitle({ className, ...props }) {
	return /* @__PURE__ */ jsx("span", {
		className: cn("truncate font-heading-medium text-text-primary", SIDEBAR_ROW_TEXT, className),
		...props
	});
}
/**
* Secondary label beneath the team name (e.g. branch or workspace name)
* in body/small typography with tertiary color.
*
* @param props - Standard span props
* @returns The rendered subtitle text
*/
function SidebarHeaderSubtitle({ className, ...props }) {
	return /* @__PURE__ */ jsx("span", {
		className: cn("truncate text-body-base leading-[16px] font-body-regular tracking-sm text-text-tertiary", className),
		...props
	});
}
/**
* Main body area that fills the remaining vertical space between the
* header and footer regions.
*
* SidebarContent handles scrolling internally via ScrollArea, while the
* footer remains pinned to the bottom edge of the sidebar.
*
* @param props - Standard div props
* @returns The rendered body container
*/
function SidebarBody({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("flex min-h-0 flex-1 flex-col overflow-hidden opacity-80", className),
		...props
	});
}
/**
* Top-aligned wrapper for the primary navigation groups.
*
* Renders an internal ScrollArea so long navigation lists can scroll
* independently while keeping SidebarFooter pinned at the bottom.
*
* @param props - Standard div props
* @returns The rendered content container
*/
function SidebarContent({ className, children, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("min-h-0 flex-1", className),
		...props,
		children: /* @__PURE__ */ jsx(ScrollArea, {
			className: "h-full w-full [--scroll-fade-color:var(--color-elevation-surface-base)]",
			children: /* @__PURE__ */ jsx(ScrollAreaViewport, {
				className: "h-full w-full",
				scrollFade: true,
				children: /* @__PURE__ */ jsx(ScrollAreaContent, {
					className: "flex w-full !min-w-0 flex-col gap-xxs pb-md",
					children
				})
			})
		})
	});
}
/**
* Bottom-pinned container for utility navigation items such as
* "Invite members", "Help & support", and "Log out".
*
* No divider: `SidebarContent`'s scroll fade already dissolves the list into
* the surface when it overflows, and a rule under a short list drew a line
* across empty space. Bottom inset comes from the nav's own `p-md`, so the
* footer sits one gutter off the edge like the header does at the top.
*
* @param props - Standard div props
* @returns The rendered footer container
*/
function SidebarFooter({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("sticky bottom-0 z-[1] mt-auto flex flex-col bg-elevation-surface-base pt-xs", className),
		...props
	});
}
/**
* Per-row entrance delay for items inside an opening `SidebarSection`:
* 40ms lead, then 35ms per row (the prototype's `0.04 + i * 0.035`). Written
* out per child so Tailwind can see the literal classes; rows past the eighth
* share the last delay. Only `SidebarItem` reads the variable, and only while
* its panel is in `data-starting-style`, so plain groups are unaffected.
*/
const sidebarGroupStaggerClassName = cn("[&>:nth-child(1)]:[--sidebar-stagger-delay:40ms]", "[&>:nth-child(2)]:[--sidebar-stagger-delay:75ms]", "[&>:nth-child(3)]:[--sidebar-stagger-delay:110ms]", "[&>:nth-child(4)]:[--sidebar-stagger-delay:145ms]", "[&>:nth-child(5)]:[--sidebar-stagger-delay:180ms]", "[&>:nth-child(6)]:[--sidebar-stagger-delay:215ms]", "[&>:nth-child(7)]:[--sidebar-stagger-delay:250ms]", "[&>:nth-child(n+8)]:[--sidebar-stagger-delay:285ms]");
/**
* Groups related navigation items together as a plain vertical stack with a
* `xxxs` (2px) gap between items in both expanded and icon-rail layouts.
* For collapsible groups with a label, use SidebarSection instead.
*
* Also assigns each child its stagger delay so items drop in one after
* another when a surrounding `SidebarSection` opens.
*
* @param props - Standard div props
* @returns The rendered group container
*/
function SidebarGroup({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("flex flex-col gap-xxxs", sidebarGroupStaggerClassName, className),
		...props
	});
}
/**
* Collapsible navigation section with a labelled trigger.
* Delegates to the Base UI Collapsible primitive for accessible
* expand/collapse behavior. Defaults to open.
*
* @param props - Base UI Collapsible.Root props including open, defaultOpen, onOpenChange
* @returns The rendered section container
*/
function SidebarSection({ className, defaultOpen = true, ...props }) {
	const { collapsed, expanding } = useSidebarCollapse();
	if (!useDelayedPresence(!collapsed)) return null;
	return /* @__PURE__ */ jsx("div", {
		className: cn("grid grid-rows-[1fr] transition-[grid-template-rows,opacity,margin-top] duration-(--sidebar-duration) ease-(--sidebar-ease)", expanding && "starting:opacity-0", "not-first:mt-sm", "group-data-[collapsed=true]/sidebar:pointer-events-none group-data-[collapsed=true]/sidebar:-mt-xxs group-data-[collapsed=true]/sidebar:grid-rows-[0fr] group-data-[collapsed=true]/sidebar:opacity-0"),
		children: /* @__PURE__ */ jsx(Collapsible.Root, {
			className: cn("flex min-h-0 flex-col overflow-hidden", className),
			defaultOpen,
			...props
		})
	});
}
/**
* Clickable section header that toggles the section content visibility.
* Renders the label in body/medium tertiary typography with a caret icon
* that rotates to indicate expanded or collapsed state.
*
* Hidden in icon-rail mode — consumers should swap sections for group
* dropdown triggers when `useSidebarCollapse().collapsed` is true.
*
* @param props - Trigger props with a required label
* @returns The rendered section label
*/
function SidebarSectionLabel({ className, children, ...props }) {
	return /* @__PURE__ */ jsxs(Collapsible.Trigger, {
		className: cn("group flex w-full cursor-pointer items-center gap-xxs border-0 bg-transparent p-xs", "font-heading-medium text-text-tertiary", SIDEBAR_ROW_TEXT, "transition-opacity duration-(--sidebar-duration) ease-(--sidebar-ease)", "group-data-[collapsed=true]/sidebar:pointer-events-none group-data-[collapsed=true]/sidebar:opacity-0", className),
		...props,
		children: [/* @__PURE__ */ jsx("span", {
			className: "min-w-0 flex-1 truncate text-left",
			children
		}), /* @__PURE__ */ jsx(CaretDownIcon, { className: "size-[12px] shrink-0 -rotate-90 text-icon-tertiary transition-transform duration-[160ms] ease-(--sidebar-caret-ease) group-data-[panel-open]:rotate-0" })]
	});
}
/**
* Animated panel that contains the section's nav items.
* Uses Base UI's height transition for smooth expand/collapse animation.
*
* Opens over 280ms and closes over 200ms on the shared emphasized curve.
* Exposes `group/panel` so descendant `SidebarItem`s can key their staggered
* drop-in / lift-out off the panel's `data-starting-style` and
* `data-ending-style` phases.
*
* @param props - Base UI Collapsible.Panel props
* @returns The rendered section content panel
*/
function SidebarSectionContent({ className, ...props }) {
	return /* @__PURE__ */ jsx(Collapsible.Panel, {
		className: cn("group/panel flex flex-col overflow-hidden", "h-[var(--collapsible-panel-height)] transition-[height] duration-[280ms] ease-(--sidebar-ease)", "data-[ending-style]:h-0 data-[ending-style]:duration-200 data-[starting-style]:h-0", className),
		...props
	});
}
const sidebarItemVariants = cva(cn("flex box-border min-w-0 w-full cursor-pointer items-center gap-xs rounded-sm border-0 px-xs py-[7px] no-underline hover:no-underline", "font-heading-medium text-text-primary", SIDEBAR_ROW_TEXT, "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-text-selected"), {
	defaultVariants: { active: false },
	variants: { active: {
		false: "bg-transparent hover:bg-elevation-surface-base-hover active:bg-elevation-surface-base-pressed",
		true: "bg-elevation-surface-base-pressed"
	} }
});
/**
* Individual navigation tab inside the sidebar.
*
* Supports an optional leading icon, an optional trailing count badge, and
* active/hover/pressed states following the Figma spec. When `icon` is a
* Phosphor element, active items receive `weight="fill"` automatically.
*
* Supports polymorphic rendering via the `render` prop, allowing items
* to render as anchors or router links for real navigation.
*
* @param props - Item props including icon, badge, active state, and optional render override
* @returns The rendered navigation item
*
* @example
* ```tsx
* <SidebarItem icon={<PackageIcon />} render={<Link href="/orders" />}>Orders</SidebarItem>
* <SidebarItem icon={<NotepadIcon />} render={<a href="/planner" />}>Planner</SidebarItem>
* ```
*/
function SidebarItem(props) {
	const { active = false, icon, badge, children, className, render, ref, tooltip, ...rest } = props;
	const state = { active };
	const { collapsed, expanding } = useSidebarCollapse();
	const labelMounted = useDelayedPresence(!collapsed);
	const railAriaLabel = collapsed && rest["aria-label"] === void 0 && typeof children === "string" ? children : void 0;
	const renderIcon = () => {
		if (!icon) return null;
		if (React.isValidElement(icon)) return /* @__PURE__ */ jsx("span", {
			className: "flex size-md shrink-0 items-center justify-center",
			children: React.cloneElement(icon, {
				className: cn("size-md shrink-0 text-icon-primary", icon.props.className),
				weight: active ? "fill" : icon.props.weight ?? "regular"
			})
		});
		return /* @__PURE__ */ jsx("span", {
			className: "flex size-md shrink-0 items-center justify-center",
			children: icon
		});
	};
	const element = useRender({
		defaultTagName: "button",
		props: {
			"children": /* @__PURE__ */ jsxs(Fragment, { children: [
				renderIcon(),
				labelMounted && /* @__PURE__ */ jsx("span", {
					className: cn("min-w-0 flex-1 truncate text-left whitespace-nowrap", sidebarCollapseFadeVariants({
						collapsed,
						entering: expanding
					})),
					children
				}),
				badge != null && labelMounted && /* @__PURE__ */ jsx(SidebarBadge, {
					className: sidebarCollapseFadeVariants({
						collapsed,
						entering: expanding
					}),
					children: badge
				})
			] }),
			"className": cn(sidebarItemVariants({ active }), "h-[calc(var(--leading-body-medium)+14px)]", "group-data-[collapsed=true]/sidebar:h-[calc(var(--spacing-md)+var(--spacing-xs)*2)]", "[transition-property:height,background-color,scale,opacity,translate]", "[transition-duration:var(--sidebar-duration),150ms,120ms,320ms,320ms]", "[transition-timing-function:var(--sidebar-ease)]", "[transition-delay:0s,0s,0s,var(--sidebar-stagger-delay,0s),var(--sidebar-stagger-delay,0s)]", "active:scale-[0.985]", "group-data-[starting-style]/panel:-translate-y-[6px] group-data-[starting-style]/panel:opacity-0", "group-data-[ending-style]/panel:-translate-y-[10px] group-data-[ending-style]/panel:opacity-0", "group-data-[ending-style]/panel:[transition-duration:var(--sidebar-duration),150ms,120ms,200ms,200ms] group-data-[ending-style]/panel:[transition-delay:0s]", className),
			"data-active": active || void 0,
			...render ? {} : { type: "button" },
			...railAriaLabel ? { "aria-label": railAriaLabel } : {},
			...rest
		},
		ref,
		render,
		state
	});
	const tooltipContent = tooltip === void 0 ? children : tooltip;
	if (!collapsed || tooltipContent == null) return element;
	return /* @__PURE__ */ jsxs(Tooltip, { children: [/* @__PURE__ */ jsx(TooltipTrigger, { render: element }), /* @__PURE__ */ jsx(TooltipContent, {
		side: "right",
		align: "center",
		sideOffset: 12,
		children: /* @__PURE__ */ jsx(TooltipLabel, { children: tooltipContent })
	})] });
}
/**
* Small numeric counter badge displayed at the trailing edge of a nav item.
* Uses body/small typography with a subtle selected background.
*
* @param props - Standard span props; children should be the count value
* @returns The rendered badge element
*/
function SidebarBadge({ className, ...props }) {
	return /* @__PURE__ */ jsx("span", {
		className: cn("flex size-md shrink-0 items-center justify-center rounded-[2px] bg-background-selected", "text-body-small leading-body-small font-body-regular text-text-primary", className),
		...props
	});
}
//#endregion
export { SIDEBAR_COLLAPSE_DURATION_MS, SIDEBAR_PANE_DURATION_MS, Sidebar, SidebarBackButton, SidebarBadge, SidebarBody, SidebarCollapseTrigger, SidebarContent, SidebarFooter, SidebarGroup, SidebarHeader, SidebarHeaderInfo, SidebarHeaderSubtitle, SidebarHeaderTitle, SidebarItem, SidebarLogo, SidebarLogoImage, SidebarLogoInitials, SidebarPane, SidebarPanes, SidebarProvider, SidebarRailToggle, SidebarSection, SidebarSectionContent, SidebarSectionLabel, sidebarItemVariants, useSidebarCollapse };

//# sourceMappingURL=Sidebar.js.map